import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, MenuItem, Select, Slider, Stack, Switch, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowDownward, ArrowUpward, ArrowBack, ArrowForward, CenterFocusStrong, Delete, Download, Refresh,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import rackService, { CameraPosition, Rack, RackCapture } from '../services/rack.service';

const LENS_MAX = 16384;

const FAILURE_LABELS: Record<string, string> = {
  camera_unreachable: 'Caméra injoignable',
  camera_busy: 'Caméra occupée, réessayez',
  not_calibrated: "Rack non calibré : enregistrez d'abord son cadrage",
  rack_not_found: 'Marqueurs du rack introuvables',
  wrong_rack: "Ce sont les marqueurs d'un autre rack",
  no_sheet: 'Pas de feuille dans le rack',
};

const errorMessage = (error: any) => error?.response?.data?.error || error?.message || 'Erreur';

const CameraRacks: React.FC = () => {
  const [racks, setRacks] = useState<Rack[]>([]);
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);
  const [position, setPosition] = useState<CameraPosition | null>(null);
  const [step, setStep] = useState(20);
  const [newNumber, setNewNumber] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [capture, setCapture] = useState<RackCapture | null>(null);
  const snapshotRef = useRef<string | null>(null);

  const loadRacks = useCallback(async () => {
    try {
      setRacks(await rackService.list());
    } catch (error) {
      toast.error(errorMessage(error));
    }
  }, []);

  const refreshSnapshot = useCallback(async () => {
    setSnapshotLoading(true);
    try {
      const url = URL.createObjectURL(await rackService.snapshot());
      if (snapshotRef.current) URL.revokeObjectURL(snapshotRef.current);
      snapshotRef.current = url;
      setSnapshotUrl(url);
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRacks();
    rackService.position().then(setPosition).catch((error) => toast.error(errorMessage(error)));
    refreshSnapshot();
    return () => {
      if (snapshotRef.current) URL.revokeObjectURL(snapshotRef.current);
    };
  }, [loadRacks, refreshSnapshot]);

  // Suivi de la capture en cours (calibration ou test) jusqu'à la fin.
  useEffect(() => {
    if (!capture || capture.status === 'done' || capture.status === 'failed') return;
    const timer = window.setTimeout(async () => {
      try {
        const next = await rackService.capture(capture.id);
        setCapture(next);
        if (next.status === 'done' || next.status === 'failed') loadRacks();
      } catch (error) {
        toast.error(errorMessage(error));
      }
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [capture, loadRacks]);

  const move = async (payload: Parameters<typeof rackService.move>[0]) => {
    try {
      setPosition(await rackService.move(payload));
      await refreshSnapshot();
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const addRack = async () => {
    try {
      await rackService.create({ number: Number(newNumber), label: newLabel });
      setNewNumber('');
      setNewLabel('');
      loadRacks();
    } catch (error: any) {
      toast.error(JSON.stringify(error?.response?.data) || errorMessage(error));
    }
  };

  const removeRack = async (rack: Rack) => {
    if (!window.confirm(`Supprimer le rack ${rack.number} ?`)) return;
    await rackService.remove(rack.id);
    loadRacks();
  };

  const downloadMarkers = async () => {
    try {
      const url = URL.createObjectURL(await rackService.markersPdf());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'marqueurs_racks.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const downloadDeskQr = async () => {
    try {
      const url = URL.createObjectURL(await rackService.deskQrPdf());
      const link = document.createElement('a');
      link.href = url;
      link.download = 'qr_places.pdf';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errorMessage(error));
    }
  };

  const running = capture !== null && (capture.status === 'queued' || capture.status === 'running');

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Caméra et racks</Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
        <Card sx={{ flex: 1, width: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">Aperçu</Typography>
              <Button startIcon={<Refresh />} onClick={refreshSnapshot} disabled={snapshotLoading}>
                Rafraîchir
              </Button>
            </Stack>
            <Box sx={{ position: 'relative', bgcolor: 'grey.900', aspectRatio: '16 / 9', mb: 2 }}>
              {snapshotUrl && (
                <img src={snapshotUrl} alt="Aperçu caméra" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
              )}
              {snapshotLoading && (
                <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', ml: -2.5, mt: -2.5 }} />
              )}
            </Box>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 40px)', gap: 0.5 }}>
                <span />
                <IconButton onClick={() => move({ tilt_delta: step })}><ArrowUpward /></IconButton>
                <span />
                <IconButton onClick={() => move({ pan_delta: -step })}><ArrowBack /></IconButton>
                <span />
                <IconButton onClick={() => move({ pan_delta: step })}><ArrowForward /></IconButton>
                <span />
                <IconButton onClick={() => move({ tilt_delta: -step })}><ArrowDownward /></IconButton>
                <span />
              </Box>
              <Select size="small" value={step} onChange={(e) => setStep(Number(e.target.value))}>
                <MenuItem value={5}>Pas fin (5)</MenuItem>
                <MenuItem value={20}>Pas moyen (20)</MenuItem>
                <MenuItem value={80}>Pas large (80)</MenuItem>
              </Select>
              <Tooltip title="Mise au point automatique ponctuelle">
                <IconButton onClick={() => move({ one_push_af: true })}><CenterFocusStrong /></IconButton>
              </Tooltip>
            </Stack>
            {position && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">Zoom ({position.zoom})</Typography>
                <Slider
                  key={`zoom-${position.zoom}`}
                  defaultValue={position.zoom} min={0} max={LENS_MAX} step={64}
                  onChangeCommitted={(_, value) => move({ zoom: value as number })}
                />
                <FormControlLabel
                  control={<Switch checked={position.focus_auto} onChange={(e) => move({ focus_auto: e.target.checked })} />}
                  label="Mise au point automatique"
                />
                <Typography variant="body2">Mise au point ({position.focus})</Typography>
                <Slider
                  key={`focus-${position.focus}`}
                  defaultValue={position.focus} min={0} max={LENS_MAX} step={32} disabled={position.focus_auto}
                  onChangeCommitted={(_, value) => move({ focus: value as number, focus_auto: false })}
                />
                <Typography variant="caption" color="text.secondary">
                  Position brute : pan {position.pan}, tilt {position.tilt}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1, width: '100%' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Typography variant="subtitle1">Racks</Typography>
              <Button startIcon={<Download />} onClick={downloadMarkers}>Marqueurs (PDF)</Button>
              <Button startIcon={<Download />} onClick={downloadDeskQr}>QR des places (PDF)</Button>
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
              Cadrez un rack avec l'aperçu (les 4 marqueurs doivent être visibles), puis « Enregistrer le cadrage ».
              La caméra bouge légèrement pour mesurer, puis revient.
            </Alert>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>N°</TableCell>
                  <TableCell>Libellé</TableCell>
                  <TableCell>État</TableCell>
                  <TableCell>Élève installé</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {racks.map((rack) => (
                  <TableRow key={rack.id}>
                    <TableCell>{rack.number}</TableCell>
                    <TableCell>{rack.label}</TableCell>
                    <TableCell>
                      {!rack.is_calibrated && <Chip size="small" label="Non calibré" />}
                      {rack.is_calibrated && !rack.needs_recalibration && (
                        <Chip size="small" color="success" label={`Calibré (dérive ${Math.round(rack.drift_total)} px)`} />
                      )}
                      {rack.needs_recalibration && (
                        <Chip size="small" color="warning" label={`À recalibrer (dérive ${Math.round(rack.drift_total)} px)`} />
                      )}
                    </TableCell>
                    <TableCell>
                      {rack.current_student
                        ? <Chip size="small" color="primary" label={rack.current_student} />
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" disabled={running}
                          onClick={async () => setCapture(await rackService.calibrate(rack.id))}>
                          Enregistrer le cadrage
                        </Button>
                        <Button size="small" disabled={running || !rack.is_calibrated}
                          onClick={async () => setCapture(await rackService.test(rack.id))}>
                          Tester
                        </Button>
                        <Button size="small" disabled={running || !rack.is_calibrated}
                          onClick={async () => setCapture(await rackService.view(rack.id))}>
                          Voir cette position en ce moment
                        </Button>
                        <IconButton size="small" onClick={() => removeRack(rack)}><Delete fontSize="small" /></IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <TextField size="small" label="N°" type="number" value={newNumber}
                onChange={(e) => setNewNumber(e.target.value)} sx={{ width: 90 }} inputProps={{ min: 1, max: 24 }} />
              <TextField size="small" label="Libellé" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
              <Button variant="contained" onClick={addRack} disabled={!newNumber}>Ajouter</Button>
            </Stack>
          </CardContent>
        </Card>
      </Stack>

      <Dialog open={capture !== null} onClose={() => !running && setCapture(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {capture?.kind === 'calibration' ? 'Enregistrement du cadrage'
            : capture?.kind === 'view' ? 'Position en ce moment' : 'Test'} — rack {capture?.rack_number}
        </DialogTitle>
        <DialogContent>
          {running && (
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={24} />
              <Typography>{capture?.status === 'queued' ? 'En attente de la caméra…' : 'Prise de vue en cours…'}</Typography>
            </Stack>
          )}
          {capture?.status === 'failed' && (
            <Alert severity="error">
              {FAILURE_LABELS[capture.failure_reason] || capture.failure_reason}
              {capture.detail && ` (${capture.detail})`}
            </Alert>
          )}
          {capture?.status === 'done' && (
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                {capture.kind === 'calibration' ? 'Cadrage enregistré.'
                  : capture.kind === 'view' ? 'Photo prise à la position enregistrée du rack.' : 'Photo du rack obtenue.'}
                {capture.recentered && ` Recentrage automatique (dérive ${Math.round(capture.drift_px || 0)} px).`}
              </Alert>
              {capture.kind === 'view' && capture.photo_raw_url && (
                <img src={capture.photo_raw_url} alt="Vue de la caméra" style={{ width: '100%', marginBottom: 8 }} />
              )}
              {capture.kind === 'view' && capture.photo_rectified_url && (
                <Typography variant="caption" color="text.secondary">Zone du rack redressée :</Typography>
              )}
              {capture.photo_rectified_url && (
                <img src={capture.photo_rectified_url} alt="Rack redressé" style={{ width: '100%' }} />
              )}
              {capture.kind === 'view' && !capture.photo_rectified_url && (
                <Alert severity="info">Marqueurs du rack non visibles : pas de vue redressée.</Alert>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCapture(null)} disabled={running}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CameraRacks;
